import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Save,
  Plus,
  X,
  Trash2,
  GripVertical,
  Laptop,
  Users as UsersIcon,
} from 'lucide-react';
import api from '../services/api';
import type { Patient } from '../../../shared/professional-types';
import type { CreateSessionDTO, SessionType } from '../../../shared/session-types';

interface Game {
  id: number;
  codigo: string;
  nombre: string;
  icono: string | null;
  dificultades: Difficulty[];
}

interface Difficulty {
  id: number;
  juego_id: number;
  nombre: string;
  icono: string | null;
  orden: number;
}

interface SelectedGame {
  juego_id: number;
  juego_nombre: string;
  dificultad_id: number;
  dificultad_nombre: string;
  orden: number;
}

export default function CreateSessionPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [games, setGames] = useState<Game[]>([]);

  // Formulario
  const [pacienteId, setPacienteId] = useState<number | null>(null);
  const [tipoSesion, setTipoSesion] = useState<SessionType>('remota');
  const [objetivos, setObjetivos] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [selectedGames, setSelectedGames] = useState<SelectedGame[]>([]);

  // Selector de juegos
  const [showGameSelector, setShowGameSelector] = useState(false);
  const [selectedGameId, setSelectedGameId] = useState<number | null>(null);
  const [selectedDifficultyId, setSelectedDifficultyId] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [patientsRes, gamesRes] = await Promise.all([
        api.get('/professional/patients'),
        api.get('/professional/games'),
      ]);

      if (patientsRes.data.ok) {
        setPatients(patientsRes.data.data);
      }

      if (gamesRes.data.ok) {
        setGames(gamesRes.data.data);
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      alert('Error al cargar datos necesarios');
    } finally {
      setLoading(false);
    }
  };

  const handleAddGame = () => {
    if (!selectedGameId || !selectedDifficultyId) {
      alert('Selecciona un juego y una dificultad');
      return;
    }

    const game = games.find((g) => g.id === selectedGameId);
    const difficulty = game?.dificultades.find((d) => d.id === selectedDifficultyId);

    if (!game || !difficulty) return;

    const newGame: SelectedGame = {
      juego_id: game.id,
      juego_nombre: game.nombre,
      dificultad_id: difficulty.id,
      dificultad_nombre: difficulty.nombre,
      orden: selectedGames.length + 1,
    };

    setSelectedGames([...selectedGames, newGame]);
    setSelectedGameId(null);
    setSelectedDifficultyId(null);
    setShowGameSelector(false);
  };

  const handleRemoveGame = (index: number) => {
    const newGames = selectedGames.filter((_, i) => i !== index);
    // Reordenar
    const reordered = newGames.map((game, i) => ({ ...game, orden: i + 1 }));
    setSelectedGames(reordered);
  };

  const handleMoveGame = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === selectedGames.length - 1)
    ) {
      return;
    }

    const newGames = [...selectedGames];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    // Intercambiar
    [newGames[index], newGames[targetIndex]] = [newGames[targetIndex], newGames[index]];

    // Reordenar
    const reordered = newGames.map((game, i) => ({ ...game, orden: i + 1 }));
    setSelectedGames(reordered);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!pacienteId) {
      alert('Selecciona un paciente');
      return;
    }

    // Solo validar juegos para sesiones remotas
    if (tipoSesion === 'remota' && selectedGames.length === 0) {
      alert('Agrega al menos un juego a la sesión remota');
      return;
    }

    // Para sesiones presenciales, no se envían juegos
    const data: CreateSessionDTO = {
      paciente_id: pacienteId,
      tipo_sesion: tipoSesion,
      objetivos: objetivos || undefined,
      observaciones_iniciales: observaciones || undefined,
      juegos: tipoSesion === 'remota' ? selectedGames.map((g) => ({
        juego_id: g.juego_id,
        dificultad_id: g.dificultad_id,
        orden: g.orden,
      })) : [],
    };

    try {
      setSaving(true);
      const response = await api.post('/professional/sessions', data);

      if (response.data.ok) {
        const sesion_id = response.data.data.id;

        // Si es presencial, redirigir a la pantalla de sesión activa
        if (tipoSesion === 'presencial') {
          navigate(`/profesional/sesiones/presencial/${sesion_id}`);
        } else {
          alert('✅ Sesión remota creada correctamente');
          navigate('/profesional/sesiones');
        }
      }
    } catch (error: any) {
      console.error('Error al crear sesión:', error);
      alert(error.response?.data?.error || 'Error al crear sesión');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-neureka-yellow"></div>
          <p className="mt-4 text-white/70">Cargando...</p>
        </div>
      </div>
    );
  }

  const selectedGame = games.find((g) => g.id === selectedGameId);

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="container mx-auto max-w-4xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate('/profesional/sesiones')}
            className="flex items-center gap-2 text-white/70 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver a Sesiones
          </button>
          <h1 className="text-4xl font-display font-bold mb-2">Nueva Sesión</h1>
          <p className="text-white/70">Crea una nueva sesión de trabajo para un paciente</p>
        </motion.div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onSubmit={handleSubmit}
          className="space-y-6"
        >
          {/* Paciente */}
          <div className="glass-container">
            <h2 className="text-xl font-bold mb-4">Información Básica</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Paciente *
                </label>
                <select
                  value={pacienteId || ''}
                  onChange={(e) => setPacienteId(parseInt(e.target.value))}
                  className="input-neureka w-full"
                  required
                >
                  <option value="">Selecciona un paciente</option>
                  {patients.map((patient) => (
                    <option key={patient.id} value={patient.id}>
                      {patient.first_name} {patient.last_name} (@{patient.username})
                    </option>
                  ))}
                </select>
              </div>

              {/* Tipo de Sesión */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Tipo de Sesión *
                </label>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setTipoSesion('remota')}
                    className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                      tipoSesion === 'remota'
                        ? 'border-neureka-yellow bg-neureka-yellow/10'
                        : 'border-white/10 bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <Laptop className="w-6 h-6 mx-auto mb-2" />
                    <span className="font-semibold">Remota</span>
                    <p className="text-xs text-white/60 mt-1">El paciente juega desde casa</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setTipoSesion('presencial')}
                    className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                      tipoSesion === 'presencial'
                        ? 'border-neureka-yellow bg-neureka-yellow/10'
                        : 'border-white/10 bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <UsersIcon className="w-6 h-6 mx-auto mb-2" />
                    <span className="font-semibold">Presencial</span>
                    <p className="text-xs text-white/60 mt-1">Sesión en la consulta</p>
                  </button>
                </div>
              </div>

              {/* Objetivos */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Objetivos de la Sesión
                </label>
                <textarea
                  value={objetivos}
                  onChange={(e) => setObjetivos(e.target.value)}
                  placeholder="Describe los objetivos terapéuticos de esta sesión..."
                  className="input-neureka w-full h-24 resize-none"
                />
              </div>

              {/* Observaciones */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Observaciones Iniciales
                </label>
                <textarea
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  placeholder="Notas o comentarios previos a la sesión..."
                  className="input-neureka w-full h-24 resize-none"
                />
              </div>
            </div>
          </div>

          {/* Juegos - Solo para sesiones remotas */}
          {tipoSesion === 'remota' && (
            <div className="glass-container">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Juegos de la Sesión</h2>
                <button
                  type="button"
                  onClick={() => setShowGameSelector(!showGameSelector)}
                  className="btn-neureka-secondary text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Agregar Juego
                </button>
              </div>

            {/* Selector de Juegos */}
            {showGameSelector && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-4 p-4 rounded-lg bg-white/5 border border-white/10"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Juego</label>
                    <select
                      value={selectedGameId || ''}
                      onChange={(e) => {
                        setSelectedGameId(parseInt(e.target.value));
                        setSelectedDifficultyId(null);
                      }}
                      className="input-neureka w-full"
                    >
                      <option value="">Selecciona un juego</option>
                      {games.map((game) => (
                        <option key={game.id} value={game.id}>
                          {game.icono} {game.nombre}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Dificultad</label>
                    <select
                      value={selectedDifficultyId || ''}
                      onChange={(e) => setSelectedDifficultyId(parseInt(e.target.value))}
                      className="input-neureka w-full"
                      disabled={!selectedGameId}
                    >
                      <option value="">Selecciona dificultad</option>
                      {selectedGame?.dificultades.map((diff) => (
                        <option key={diff.id} value={diff.id}>
                          {diff.icono} {diff.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowGameSelector(false)}
                    className="flex-1 btn-neureka-secondary text-sm"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleAddGame}
                    className="flex-1 btn-neureka text-sm"
                  >
                    Agregar
                  </button>
                </div>
              </motion.div>
            )}

            {/* Lista de Juegos Seleccionados */}
            {selectedGames.length === 0 ? (
              <div className="text-center py-12 text-white/50">
                <p>No hay juegos agregados aún</p>
                <p className="text-sm mt-2">Haz clic en "Agregar Juego" para comenzar</p>
              </div>
            ) : (
              <div className="space-y-2">
                {selectedGames.map((game, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        onClick={() => handleMoveGame(index, 'up')}
                        disabled={index === 0}
                        className="text-white/50 hover:text-white disabled:opacity-30"
                      >
                        <GripVertical className="w-4 h-4 rotate-90" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveGame(index, 'down')}
                        disabled={index === selectedGames.length - 1}
                        className="text-white/50 hover:text-white disabled:opacity-30"
                      >
                        <GripVertical className="w-4 h-4 -rotate-90" />
                      </button>
                    </div>

                    <div className="flex-1">
                      <div className="font-semibold">{game.juego_nombre}</div>
                      <div className="text-sm text-white/60">
                        Dificultad: {game.dificultad_nombre}
                      </div>
                    </div>

                    <div className="text-sm text-white/50">#{game.orden}</div>

                    <button
                      type="button"
                      onClick={() => handleRemoveGame(index)}
                      className="p-2 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            </div>
          )}

          {/* Nota para sesiones presenciales */}
          {tipoSesion === 'presencial' && (
            <div className="glass-container">
              <div className="p-4 rounded-lg bg-orange-500/10 border border-orange-500/30">
                <p className="text-orange-300 text-sm">
                  <strong>ℹ️ Sesión Presencial:</strong> Los juegos se seleccionarán durante la sesión presencial.
                  Al crear la sesión, se abrirá la pantalla de sesión activa donde podrás elegir y jugar los juegos en tiempo real.
                </p>
              </div>
            </div>
          )}

          {/* Botones */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/profesional/sesiones')}
              className="flex-1 btn-neureka-secondary"
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 btn-neureka"
              disabled={saving || !pacienteId || (tipoSesion === 'remota' && selectedGames.length === 0)}
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Crear Sesión
                </>
              )}
            </button>
          </div>
        </motion.form>
      </div>
    </div>
  );
}
